import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * Minimum breathing room, in px, to keep between the last nav link and the
 * centred logo before giving up on the inline menu and using the drawer.
 */
const MENU_LOGO_GAP = 16;

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Debounce handle for the menu/drawer re-measure
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #remeasureTimer;

  /**
   * window.innerWidth as of the last resize-observer tick, used to tell a
   * genuine viewport resize apart from the header's own box size changing
   * because its content (the nav row) just toggled visibility — without
   * this, re-attempting the menu on every observer tick creates a
   * feedback loop: showing the menu changes the header's height, which
   * re-fires the observer, which (if it still doesn't fit) hides the
   * menu again, which changes the height again, forever.
   * @type {number}
   */
  #lastWindowWidth = window.innerWidth;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Re-decide menu vs drawer whenever the viewport width actually
    // changes. Gated on window.innerWidth rather than firing on every tick
    // of this observer: it watches the header's own box, and showing or
    // hiding the nav resizes that box too, so an ungated retry self-
    // triggers (show → resize → hide → resize → show …) and the header
    // visibly flickers.
    const currentWidth = window.innerWidth;
    if (currentWidth !== this.#lastWindowWidth) {
      this.#lastWindowWidth = currentWidth;
      // Debounced: this observer fires repeatedly through a drag-resize and
      // the intermediate frames report half-updated geometry, which measures
      // to the wrong verdict. Wait for the size to settle, then decide once.
      clearTimeout(this.#remeasureTimer);
      this.#remeasureTimer = setTimeout(() => this.#remeasureMenuFit(), 150);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Re-decides menu vs drawer from a real measurement.
   *
   * overflow-list can only measure what's rendered, and the drawer state
   * hides the menu outright — so once we're in the drawer every rect is 0,
   * nothing ever reports overflow, and it can never get back out however
   * much room appears. Show the menu first, let it lay out, then measure
   * and apply the verdict.
   */
  #remeasureMenuFit() {
    const overflowList = this.querySelector('overflow-list');
    if (!overflowList) return;

    // Show the menu before measuring — the drawer state hides it, and a
    // hidden element measures as zero width, which would always look like
    // it fits and leave us unable to ever come back out of the drawer.
    this.dataset.menuStyle = 'menu';

    // Reading offsetWidth forces the browser to lay the just-revealed menu
    // out now, so the measurements below are real rather than stale zeros.
    // Deliberately not requestAnimationFrame: rAF is suspended while the
    // tab isn't being composited, so a page opened in a background tab
    // would never measure at all and would sit on the wrong verdict until
    // it happened to be resized.
    void overflowList.offsetWidth;

    const links = /** @type {HTMLElement[]} */ ([...overflowList.children]).filter(
      (el) => el.tagName === 'LI' && !el.hasAttribute('slot')
    );
    const lastLink = links[links.length - 1];
    if (!lastLink) return;

    // Test the thing that actually goes wrong — the nav running into the
    // centred logo — rather than comparing against the overflow-list's own
    // box. That box is narrower than the space the links really get (the
    // drawer and search buttons share the column and give their width back
    // when the menu appears), so measuring against it rejects layouts that
    // in fact fit perfectly well.
    const logo = this.querySelector('.header__column--center');
    const navEnd = lastLink.getBoundingClientRect().right;
    const limit = logo ? logo.getBoundingClientRect().left - MENU_LOGO_GAP : overflowList.getBoundingClientRect().right;

    this.#updateMenuVisibility(navEnd > limit);
  }

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    this.dataset.menuStyle = hideMenu ? 'drawer' : 'menu';
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = getScrollTop();
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    // overflow-list measures once during initial layout, and the nav's
    // webfonts load with font-display: swap — so that measurement can run
    // against fallback metrics and conclude everything fits when it
    // doesn't. Nothing re-measures unless the element's box changes, so on
    // a page that's loaded and never resized the wrong verdict sticks and
    // the links sit overlapping the logo. Ask it to measure again once the
    // real fonts are in; it re-fires overflowMinimum, which the listener
    // above turns into the correct menu/drawer choice.
    // Re-measure once things that change the outcome have settled. Both
    // triggers are needed: fonts.ready covers the swap from fallback
    // metrics, but it can resolve before <overflow-list> has finished its
    // own async setup (it awaits its stylesheet), in which case the reflow
    // lands on a component that isn't listening yet. window load is late
    // enough that it always is.
    const remeasure = () => this.#remeasureMenuFit();
    document.fonts.ready.then(remeasure);
    if (document.readyState === 'complete') remeasure();
    else window.addEventListener('load', remeasure, { once: true });

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
